import React, { useState, useEffect, useCallback } from 'react'
import { COUNTDOWN_SECONDS } from '@/constant'
import { isEmpty } from '@/utils'
import { Button, TextField } from '@mui/material'
import { UserLogin } from '@/types'
import { LoadingButton } from '@/components/Button'
import { BasicCard, Footer, ValidationButton, FlexRow, ErrorText } from './components'
import Modal from '@/components/Modal'
import Text from '@/components/Text'
import Form, { FormItem } from '@/components/Form'
import { useMutation } from '@tanstack/react-query'
import { getBindMailCode, bindMail } from '@/api'
import { Message } from '@cx/ui'

export interface BindEmailProps {
  open: boolean
  onClose: () => void
}

export type FormValueProps = Omit<UserLogin, 'nickname' | 'password'>

const BindEmail: React.FC<BindEmailProps> = (props) => {
  const { open, onClose } = props
  const [formValue, setFormValue] = useState<FormValueProps>({
    mail: '',
    // @ts-ignore
    code: null,
  })
  const [hasError, setHasError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [validating, setValidation] = useState<boolean>(false)

  const bindMailMutation = useMutation(bindMail, {
    onError: (error) => {
      setValidation(false)
      setLoading(false)
    },
    onSuccess: () => {
      Message.success('绑定成功')
      setValidation(false)
      setLoading(false)
      onClose()
    },
    onMutate: () => {
      setValidation(true)
      setLoading(true)
    },
  })

  const handleFormChange = useCallback((formValue: FormValueProps) => {
    setFormValue(formValue)
  }, [])

  const handleValidations = useCallback((hasErrors: boolean) => {
    setHasError(hasErrors)
  }, [])

  const handleBind = useCallback(() => {
    // @ts-ignore
    bindMailMutation.mutate(formValue)
  }, [bindMailMutation, formValue])

  return (
    <Modal width={500} open={open} onClose={onClose}>
      <BasicCard>
        <Text>绑定邮箱</Text>
        <Form
          initialValue={formValue}
          // @ts-ignore
          onFormValueChange={handleFormChange}
          // @ts-ignore
          onValidate={handleValidations}
          validating={validating}
        >
          <FormItem
            label='mail'
            text={'邮箱'}
            rules={{
              errorMessage: '邮箱不能为空',
              validator: (value: string) => value?.length > 0,
            }}
          >
            <TextField placeholder={'请输入邮箱'} fullWidth={true} type='text' />
          </FormItem>
          <FormItem
            label='code'
            text={'验证码'}
            rules={{
              errorMessage: '验证码不能为空',
              validator: (value: string) => value?.length > 0,
            }}
          >
            <ValidationCode mail={formValue?.mail} />
          </FormItem>
        </Form>
        <Footer>
          <Button variant='outlined' onClick={onClose}>
            取消
          </Button>
          <LoadingButton loading={loading} disabled={hasError} onClick={handleBind}>
            确认绑定
          </LoadingButton>
        </Footer>
      </BasicCard>
    </Modal>
  )
}

export const ValidationCode = ({
  mail,
  value,
  label,
  onChange,
}: {
  mail?: string
  label?: string
  value?: unknown
  onChange?: () => void
}) => {
  const [second, setSecond] = useState<number>(COUNTDOWN_SECONDS)
  const [timing, setTiming] = useState(false)
  const [sended, setSended] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const mailCodeMutation = useMutation(getBindMailCode, {
    onError: (error: string) => {
      setErrorMessage(error ?? '邮箱无效')
      setTiming(false)
      setSended(false)
    },
    onSuccess: () => {
      Message.success('验证码已发送至邮箱')
      setTiming(true)
    },
    onMutate: () => {
      setErrorMessage('')
      setSended(true)
    },
  })

  const handleGetCode = useCallback(() => {
    // @ts-ignore
    mailCodeMutation.mutate({ mail })
  }, [mail, mailCodeMutation])

  useEffect(() => {
    let timer: NodeJS.Timeout
    function countdown() {
      setSecond((preSecond) => {
        if (preSecond <= 1) {
          setTiming(false)
          setSended(true)
          return COUNTDOWN_SECONDS
        } else {
          timer = setTimeout(countdown, 1000)
          return preSecond - 1
        }
      })
    }
    if (timing) {
      timer = setTimeout(countdown, 1000)
    }
    return () => clearTimeout(timer)
  }, [timing])

  return (
    <>
      <FlexRow sx={{ justifyContent: 'space-between' }}>
        <TextField label={label} placeholder={'请输验证码'} type='text' value={value} onChange={onChange} />
        <ValidationButton disabled={sended || isEmpty(mail)} onClick={handleGetCode}>
          {sended ? `再次发送：${second}s` : '发送验证码'}
        </ValidationButton>
      </FlexRow>
      <ErrorText>{errorMessage}</ErrorText>
    </>
  )
}

export default BindEmail
